var { initIframePrint } = require('./shared/iframe-print');

/**
 * Itinerary Planner - Scroll Position Management and Clear Confirmation
 * Handles scroll preservation during form submissions and clear confirmation modal
 */

(function() {
    'use strict';

    /**
     * Initialize the itinerary planner
     */
    function init() {
        restoreScrollPosition();

        const itineraryForm = document.getElementById('itineraryForm');
        if (itineraryForm) {
            itineraryForm.addEventListener('submit', saveScrollPosition);
        }

        // Attach scroll to top handler
        const scrollToTopBtn = document.getElementById('scrollToTopBtn');
        if (scrollToTopBtn) {
            scrollToTopBtn.addEventListener('click', function() {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }

        initClearConfirmation();

        initIframePrint({
            formId: 'ipPrintForm',
            iframeId: 'ipPrintFrame',
            buttonId: 'ipPrintBtn',
            readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Itinerary',
            beforeSubmit: gatherFormDataForPrint,
        });
    }

    /**
     * Gathers current form data and updates the print form's InputJson field.
     * Called before the print form is submitted to ensure the latest data is sent.
     */
    function gatherFormDataForPrint() {
        var printForm = document.getElementById('ipPrintForm');
        var mainForm = document.getElementById('itineraryForm');
        if (!printForm || !mainForm) {
            return;
        }

        var inputJsonField = printForm.querySelector('input[name="InputJson"]');
        if (!inputJsonField) {
            return;
        }

        var data = {
            tripName: getInputValue(mainForm, 'TripName'),
            tripStart: getInputValue(mainForm, 'TripStart') || null,
            tripEnd: getInputValue(mainForm, 'TripEnd') || null,
            destination: getInputValue(mainForm, 'Destination'),
            travelers: getInputValue(mainForm, 'Travelers'),
            bookingReferences: getTextareaValue(mainForm, 'BookingReferences'),
            emergencyContacts: getTextareaValue(mainForm, 'EmergencyContacts'),
            budgetNotes: getTextareaValue(mainForm, 'BudgetNotes'),
            packingChecklist: getTextareaValue(mainForm, 'PackingChecklist'),
            days: gatherDaysData(mainForm),
        };

        inputJsonField.value = JSON.stringify(data);
    }

    /**
     * Gets the value of an input field by name from the main form
     * @param {HTMLFormElement} form The form element
     * @param {string} name The input name (asp-for generates this)
     * @returns {string} The input value or empty string
     */
    function getInputValue(form, name) {
        var input = form.querySelector('input[name="' + name + '"], input[id="' + name + '"]');
        return input ? input.value : '';
    }

    /**
     * Gets the value of a textarea by name from the main form
     * @param {HTMLFormElement} form The form element
     * @param {string} name The textarea name (asp-for generates this)
     * @returns {string} The textarea value or empty string
     */
    function getTextareaValue(form, name) {
        var textarea = form.querySelector('textarea[name="' + name + '"], textarea[id="' + name + '"]');
        return textarea ? textarea.value : '';
    }

    /**
     * Gathers all itinerary day data from the form
     * @param {HTMLFormElement} form The form element
     * @returns {Array} Array of day objects matching ItineraryPlannerPrintInput.ItineraryDay
     */
    function gatherDaysData(form) {
        var days = [];
        var dayIndex = 0;

        while (true) {
            var dayNumberInput = form.querySelector('input[name="Days[' + dayIndex + '].DayNumber"]');
            if (!dayNumberInput) {
                break;
            }

            var dateInput = form.querySelector('input[name="Days[' + dayIndex + '].Date"]');
            var morningTextarea = form.querySelector('textarea[name="Days[' + dayIndex + '].MorningActivities"]');
            var afternoonTextarea = form.querySelector('textarea[name="Days[' + dayIndex + '].AfternoonActivities"]');
            var eveningTextarea = form.querySelector('textarea[name="Days[' + dayIndex + '].EveningActivities"]');
            var accommodationInput = form.querySelector('input[name="Days[' + dayIndex + '].Accommodation"]');
            var transportationInput = form.querySelector('input[name="Days[' + dayIndex + '].Transportation"]');
            var notesTextarea = form.querySelector('textarea[name="Days[' + dayIndex + '].Notes"]');

            days.push({
                dayNumber: parseInt(dayNumberInput.value, 10) || (dayIndex + 1),
                date: dateInput ? dateInput.value : null,
                morningActivities: morningTextarea ? morningTextarea.value : '',
                afternoonActivities: afternoonTextarea ? afternoonTextarea.value : '',
                eveningActivities: eveningTextarea ? eveningTextarea.value : '',
                accommodation: accommodationInput ? accommodationInput.value : '',
                transportation: transportationInput ? transportationInput.value : '',
                notes: notesTextarea ? notesTextarea.value : '',
            });

            dayIndex++;
        }

        return days;
    }

    /**
     * Save current scroll position before form submission
     * Skips saving if the confirmation modal is visible to preserve the original position
     */
    function saveScrollPosition() {
        // Don't save scroll position if the confirmation modal is visible
        // We want to restore to the position from before the modal appeared
        const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
        if (modalOverlay) {
            return;
        }

        try {
            sessionStorage.setItem('itineraryPlannerScrollPos', window.pageYOffset.toString());
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Restore scroll position after page reload
     * Only removes saved position when modal is not visible (to support Confirm/Cancel flow)
     */
    function restoreScrollPosition() {
        try {
            const scrollPos = sessionStorage.getItem('itineraryPlannerScrollPos');
            if (scrollPos) {
                window.scrollTo(0, parseInt(scrollPos, 10));
                
                // Only remove the saved position if the modal is NOT visible
                // If modal is visible, keep it for when user confirms/cancels
                const modalOverlay = document.querySelector('.clear-confirmation-overlay.is-visible');
                if (!modalOverlay) {
                    sessionStorage.removeItem('itineraryPlannerScrollPos');
                }
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Initialize clear confirmation modal controls
     */
    function initClearConfirmation() {
        const clearButton = document.getElementById('itineraryPlannerClear');
        const overlay = document.getElementById('clearConfirmationOverlay');
        if (!clearButton || !overlay) {
            return;
        }

        const closeButtons = overlay.querySelectorAll('[data-clear-confirmation="close"]');

        clearButton.addEventListener('click', function () {
            openClearConfirmation(overlay);
        });

        closeButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                closeClearConfirmation(overlay);
            });
        });
    }

    /**
     * Open the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function openClearConfirmation(overlay) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = scrollbarWidth + 'px';
        }
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('clear-confirmation-open');
    }

    /**
     * Close the clear confirmation modal
     * @param {HTMLElement} overlay The modal overlay element
     */
    function closeClearConfirmation(overlay) {
        overlay.classList.remove('is-visible');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('clear-confirmation-open');
        document.body.style.paddingRight = '';
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
